/** Parse “7 Aug vs 8 Aug” / “compare … with …” into two day ranges (pure helpers). */

import { SlateDateRange } from './slate.types';

const MONTH =
    'jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|jun(?:e)?|jul(?:y)?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?';

const MONTHS: Record<string, number> = {
    jan: 0,
    january: 0,
    feb: 1,
    february: 1,
    mar: 2,
    march: 2,
    apr: 3,
    april: 3,
    may: 4,
    jun: 5,
    june: 5,
    jul: 6,
    july: 6,
    aug: 7,
    august: 7,
    sep: 8,
    sept: 8,
    september: 8,
    oct: 9,
    october: 9,
    nov: 10,
    november: 10,
    dec: 11,
    december: 11,
};

function pad(n: number): string {
    return String(n).padStart(2, '0');
}

function ymd(d: Date): string {
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function labelDay(d: Date): string {
    return d.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
}

function dayRange(d: Date): Omit<SlateDateRange, 'inferredDefault'> {
    const s = new Date(d);
    s.setHours(0, 0, 0, 0);
    const e = new Date(d);
    e.setHours(23, 59, 59, 999);
    return {
        startDate: ymd(s),
        endDate: ymd(e),
        label: labelDay(s),
    };
}

function parseNamedDay(
    day: string,
    monthRaw: string,
    yearRaw: string | undefined,
    now: Date
): Date | null {
    const month = MONTHS[monthRaw.toLowerCase()];
    if (month == null) return null;
    const year = yearRaw ? Number(yearRaw) : now.getFullYear();
    const d = new Date(year, month, Number(day));
    if (d.getMonth() !== month || d.getDate() !== Number(day)) return null;
    return d;
}

function parseOneDayBlob(blob: string, now: Date): Date | null {
    const t = blob.trim();
    const named = t.match(
        new RegExp(
            `^(\\d{1,2})(?:st|nd|rd|th)?\\s+(${MONTH})(?:\\s*,?\\s*(\\d{4}))?$`,
            'i'
        )
    );
    if (named) return parseNamedDay(named[1], named[2], named[3], now);
    const named2 = t.match(
        new RegExp(
            `^(${MONTH})\\s+(\\d{1,2})(?:st|nd|rd|th)?(?:\\s*,?\\s*(\\d{4}))?$`,
            'i'
        )
    );
    if (named2) return parseNamedDay(named2[2], named2[1], named2[3], now);
    const iso = t.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
    if (iso) {
        const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
        return d;
    }
    return null;
}

export function extractCompareDateRanges(
    text: string,
    now = new Date()
): { a: SlateDateRange; b: SlateDateRange } | null {
    const raw = text.trim();
    if (!/\bvs\.?\b|\bversus\b|\bcompared?\s+to\b|\bcompare\b/i.test(raw)) {
        return null;
    }

    // "on 7th August 2026 vs 8th August 2026"
    const pair = raw.match(
        new RegExp(
            `(\\d{1,2}(?:st|nd|rd|th)?\\s+(?:${MONTH})(?:\\s*,?\\s*\\d{4})?)\\s+(?:vs\\.?|versus|compared?\\s+to)\\s+(\\d{1,2}(?:st|nd|rd|th)?\\s+(?:${MONTH})(?:\\s*,?\\s*\\d{4})?)`,
            'i'
        )
    );
    if (pair) {
        const da = parseOneDayBlob(pair[1], now);
        const db = parseOneDayBlob(pair[2], now);
        if (da && db) return { a: dayRange(da), b: dayRange(db) };
    }

    const pair2 = raw.match(
        /\b(\d{4}-\d{1,2}-\d{1,2})\s+(?:vs\.?|versus|compared?\s+to)\s+(\d{4}-\d{1,2}-\d{1,2})\b/i
    );
    if (pair2) {
        const da = parseOneDayBlob(pair2[1], now);
        const db = parseOneDayBlob(pair2[2], now);
        if (da && db) return { a: dayRange(da), b: dayRange(db) };
    }

    return null;
}

export function isCompareAsk(text: string): boolean {
    return !!extractCompareDateRanges(text);
}
